/**
 * Legacy session/local storage bridge — keeps `sessionStorage.currentUser`
 * (and `localStorage.currentUser` as fallback) in sync with the React shell's
 * MSAL account + client store.
 *
 * Why this exists:
 *   Legacy MFEs (`home`, `workspace`, `dashboards`, …) read user/client
 *   context directly from these storage keys at bootstrap and on every
 *   change — they were originally populated by `raptor-shell-header`
 *   (`app.component.ts:resetUserClientDetails`). Without this bridge the
 *   MFE renders an empty shell (all `*ngIf` branches gated on
 *   `currentUser.clients[?].clientId === currentlySelectedClientId`).
 *
 * Shape: must match `mapSessionStorageUserDetails` in legacy
 *   `raptor-shell-header/src/app/app.component.ts:438`.
 *
 *   {  username, fullName, firstName, lastName, emailId,
 *     recentClient, lastAcceptedPrivacyDisclaimer, isGlobal,
 *     clients: [{ clientId, clientName, isFavorite }, …]
 *   }
 *
 * NOTE: there is intentionally NO `ut` field — the legacy shell
 * (`authorize.service.mapUserDetails`) and header
 * (`app.component.mapSessionStorageUserDetails`) both omit it; user type is
 * exposed to MFEs via `ey.login.tnnt` / `EY_RAPToR_UserType`, not `currentUser`.
 *
 *   `currentlySelectedClientId` is already written by `client-store.ts` —
 *   we don't touch it here.
 */

import { msalInstance } from '@/auth/msal';
import { authModule } from '@/auth/auth-module';
import { isAuth0ShellSession } from '@/auth/auth-provider';
import {
  clearCurrentUserType,
  createServiceAccountBlockedError,
  isServiceAccountBlockedError,
  isServiceAccountProfile,
  setCurrentUserTypeFromProfile,
  subscribeUserType,
} from '@/auth/user-type';
import { clearUserProfile, getUserProfile, setUserProfileFromRum } from '@/auth/user-profile';
import { useClientStore, type ClientInfo } from '@/client/client-store';
import { http } from '@/http/http';
import { Endpoints } from '@/http/endpoints';
import { getTracker } from '@/telemetry/tracker';
import { isEnvFlagTrue } from '@/config/env-flags';

const KEY = 'currentUser';

// Legacy `raptor-shell-ui/src/app/core/constants/sessionstorage-constants.ts`
// → `USER_CONTEXT: 'ey.user.context'`. Legacy MFEs (e.g. `orchestratoruitasks`)
// read this via BRACKET access — `JSON.parse(sessionStorage['ey.user.context'])`
// — and pull `.localAccountId` to use as the user GUID for their APIs. Bracket
// access on a missing key returns `undefined` (not `null`), so an absent key
// makes the MFE throw `"undefined" is not valid JSON`. We must populate it.
const USER_CONTEXT_KEY = 'ey.user.context';

interface LegacyClient {
  clientId: string;
  clientName: string;
  isFavorite: boolean;
}

interface LegacyUser {
  username: string;
  fullName: string;
  firstName: string;
  lastName: string;
  emailId: string;
  recentClient: string;
  lastAcceptedPrivacyDisclaimer: string;
  isGlobal: boolean;
  clients: LegacyClient[];
}

/**
 * Best-effort split of MSAL `account.name` into first/last. The legacy RUM
 * payload provides these as separate fields; until we wire that endpoint we
 * derive from the AAD display name. Single-token names fall back to using
 * the same value for both — legacy MFE only uses first + last for the
 * avatar initials, so empty-string fallback is fine.
 */
function splitName(displayName: string | undefined): { firstName: string; lastName: string } {
  if (!displayName) return { firstName: '', lastName: '' };
  const parts = displayName.trim().split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function toLegacyClients(clients: readonly ClientInfo[]): LegacyClient[] {
  return clients.map((c) => ({
    clientId: c.id,
    clientName: c.name,
    isFavorite: c.isFavorite,
  }));
}

interface ShellIdentity {
  name: string;
  username: string;
  oid: string;
}

/**
 * Unified identity source. In the independent Auth0 shell (VITE_AUTH_PROVIDER=
 * auth0) there is no MSAL account, so identity comes from the provider-aware
 * `authModule.user` (which reads the Auth0 `ey.user.context`); otherwise it
 * comes from the active MSAL account (unchanged behaviour).
 */
function getShellIdentity(): ShellIdentity | null {
  if (isAuth0ShellSession()) {
    const u = authModule.user;
    if (!u) return null;
    return { name: u.name ?? '', username: u.email ?? u.principalName ?? '', oid: u.oid ?? '' };
  }
  const account = msalInstance.getActiveAccount();
  if (!account) return null;
  return {
    name: account.name ?? '',
    username: account.username ?? '',
    oid: account.localAccountId ?? account.homeAccountId ?? '',
  };
}

function buildPayload(clients: readonly ClientInfo[], currentId: string | null): LegacyUser | null {
  const identity = getShellIdentity();
  if (!identity) return null;

  // Prefer the resolved RUM profile (correct for external / guest users whose
  // display name is empty). Fall back to splitting the provider display name.
  const profile = getUserProfile();
  const fallback = splitName(identity.name);
  const firstName = profile?.firstName || fallback.firstName;
  const lastName = profile?.lastName || fallback.lastName;
  const fullName =
    profile?.name || identity.name || `${firstName} ${lastName}`.trim() || identity.username || '';
  const emailId = profile?.email || identity.username || '';
  // Legacy `mapUserDetails`: global = userRoleTypeId 6 or 7.
  const isGlobal = profile?.userRoleTypeId === 6 || profile?.userRoleTypeId === 7;
  return {
    // Legacy sets `username` to the display name (not the email/UPN).
    username: fullName,
    fullName,
    firstName,
    lastName,
    emailId,
    recentClient: profile?.recentClient || currentId || '',
    lastAcceptedPrivacyDisclaimer: profile?.lastAcceptedPrivacyDisclaimer ?? '',
    isGlobal,
    clients: toLegacyClients(clients),
  };
}

function write(payload: LegacyUser | null): void {
  if (!payload) return;
  const json = JSON.stringify(payload);
  // Mirror legacy `authorize.service.ts`: enableSessionStorage=true → session,
  // else localStorage. Legacy MFEs read `currentUser` from the same
  // flag-selected store, so write to exactly one location — and remove the copy
  // in the OTHER store so a value written while the flag was momentarily false
  // (e.g. before the yml-quote fix) can't linger as a duplicate.
  const useSession = isEnvFlagTrue(import.meta.env.VITE_ENABLE_SESSION_STORAGE);
  try {
    if (useSession) {
      sessionStorage.setItem(KEY, json);
      localStorage.removeItem(KEY);
    } else {
      localStorage.setItem(KEY, json);
      sessionStorage.removeItem(KEY);
    }
  } catch {
    /* private mode / storage full */
  }
}

/**
 * Mirror legacy `raptor-msal.service.ts:setUserContextSession`. Serializes the
 * active MSAL account (which carries `localAccountId`, `homeAccountId`,
 * `username`, `idToken`, claims, …) into `sessionStorage['ey.user.context']`,
 * exactly like the legacy shell. Written write-once (only when absent) to match
 * legacy semantics. Also replicates the legacy idToken base64url→base64 payload
 * normalisation so any MFE that `atob()`-decodes the payload keeps working.
 */
function writeUserContext(): void {
  // `ey.user.context` is session-only in legacy (`raptor-msal.service`
  // `setUserContextSession` always writes sessionStorage, regardless of the
  // enableSessionStorage flag). Drop any stale localStorage copy so it can't
  // shadow or duplicate the session value.
  try {
    localStorage.removeItem(USER_CONTEXT_KEY);
  } catch {
    /* storage unavailable */
  }
  try {
    if (sessionStorage.getItem(USER_CONTEXT_KEY) != null) return;
  } catch {
    return; /* storage unavailable */
  }

  const active = msalInstance.getActiveAccount();
  if (!active) return;

  // Clone so the idToken normalisation below does not mutate MSAL's cached
  // account reference.
  const account = { ...active };
  const idToken = account.idToken;
  if (idToken && idToken.split('.').length > 2) {
    const parts = idToken.split('.');
    if (parts[1].includes('-') || parts[1].includes('_')) {
      parts[1] = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      account.idToken = parts.join('.');
    }
  }

  try {
    sessionStorage.setItem(USER_CONTEXT_KEY, JSON.stringify(account));
  } catch {
    /* private mode */
  }
}

let started = false;
let fetchedProfileForOid: string | null = null;
let profileFetchInFlightForOid: string | null = null;
let serviceAccountBlocked = false;
const profileFetchAttempts = new Map<string, number>();

function activeOid(): string {
  return getShellIdentity()?.oid ?? '';
}

function blockServiceAccountLogin(): never {
  serviceAccountBlocked = true;
  getTracker().event('ServiceAccountLoginBlocked');
  clearCurrentUserType();
  clearUserProfile();
  try {
    sessionStorage.removeItem(KEY);
    sessionStorage.removeItem(USER_CONTEXT_KEY);
  } catch {
    /* storage unavailable */
  }
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* storage unavailable */
  }
  authModule.logout();
  throw createServiceAccountBlockedError();
}

function refreshUserTypeFromRumProfile(onResolved: () => void): void {
  const oid = activeOid();
  if (!oid || fetchedProfileForOid === oid || profileFetchInFlightForOid === oid) return;
  profileFetchInFlightForOid = oid;

  void http<unknown>(Endpoints.rumUserDetails(oid))
    .then((profile) => {
      profileFetchInFlightForOid = null;
      if (isServiceAccountProfile(profile)) {
        fetchedProfileForOid = oid;
        blockServiceAccountLogin();
      }
      setUserProfileFromRum(profile);
      if (setCurrentUserTypeFromProfile(profile)) {
        fetchedProfileForOid = oid;
        profileFetchAttempts.delete(oid);
        onResolved();
      }
    })
    .catch((error: unknown) => {
      profileFetchInFlightForOid = null;
      if (isServiceAccountBlockedError(error)) return;
      const attempts = (profileFetchAttempts.get(oid) ?? 0) + 1;
      profileFetchAttempts.set(oid, attempts);
      if (attempts <= 3) {
        window.setTimeout(() => refreshUserTypeFromRumProfile(onResolved), attempts * 1000);
      }
    });
}

/**
 * Subscribe to the client store and persist the legacy `currentUser` blob
 * on every change. Safe to call multiple times — second+ call is a no-op.
 */
export function startLegacyUserBridge(): void {
  if (started) return;
  started = true;

  const flush = () => {
    if (serviceAccountBlocked) return;
    const { available, current } = useClientStore.getState();
    write(buildPayload(available, current?.id ?? null));
    // `ey.user.context` depends only on the MSAL account, but flushing it here
    // (write-once guarded) guarantees it lands as soon as an account is active.
    writeUserContext();
    refreshUserTypeFromRumProfile(flush);
  };

  // Initial write (may be no-op if MSAL account not yet active).
  flush();

  // React to client list / selection changes.
  useClientStore.subscribe(flush);
  subscribeUserType(flush);
}
